import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Star, ShieldCheck, BookOpen, Video, NotebookPen, GraduationCap, Play, MessageSquareText, Award } from "lucide-react";
import MarketingShell from "@/components/marketing/MarketingShell";
import SleepTimeline from "@/components/marketing/SleepTimeline";
import { Crest } from "@/components/marketing/Wordmark";
import { StartHere, LearningFormats } from "@/components/marketing/StartHere";
import { http, trackEvent } from "@/lib/api";
import { useSeo, StructuredData } from "@/lib/seo";

const TRACKER_LINK = "/register?next=/portal/progress";

const FAQ = [
  ["How do I enroll as a student?",
   "Create a free student account to use the Infant Sleep Tracker and explore the Course Catalog. When you're ready, choose any course: IS-101 Foundations of Infant Sleep, NS-101 Naps, Wake Windows & Daily Rhythm, IS-201 Night Wakings & Independent Settling, IS-301 Sleep Regressions & Developmental Changes or IS-401 Bedtime, Early Waking & the Crib Transition. There is no prerequisite and no consulting purchase is required."],
  ["How does tuition work?",
   "Student registration and the Infant Sleep Tracker are free. Each course carries its own tuition, shown on its course page: Independent Study is $99 for IS-101, $149 for NS-101, and $299 each for IS-201, IS-301 and IS-401; Faculty-Led Study is $299, $349 and $399 respectively. Tuition is per family, and up to two caregivers may attend. Private consulting is never included in or required for course tuition."],
  ["When do live classes begin?",
   "Faculty-Led Study uses rolling enrollment. Enroll on any course page and your full course opens as soon as payment is confirmed. Andrea Hurley then contacts you personally to arrange your four weekly 30-minute online meetings, and the confirmed dates, time zone and meeting link appear in your dashboard. There is no minimum group size. Independent Study never requires a live class."],
  ["Who teaches the courses?",
   "Andrea Hurley, Founding Instructor and Certified Pediatric Sleep Consultant, wrote the curriculum, teaches every live class and reviews every final assessment. Faculty-led groups of one to six families meet online via Zoom or Teams."],
  ["Is this a local, in-person infant sleep consultant?",
   "Mostly, we work online, so families anywhere can use our free guides, take a course or book virtual consulting with Andrea. In-home consulting is only available in the Denver metro area. Andrea also offers private consulting through The Nurtured Parent Co., her separate practice."],
  ["Which business am I buying from?",
   "Courses and the consulting services on our Sleep Help page are purchased from Infant Sleep Consultant Near Me. If you book through The Nurtured Parent Co., you're working with Andrea's separate private practice, and that purchase happens on its own website."],
  ["Do I need private consulting to take a course?",
   "No. Every course can be completed through its lessons, readings and coursework alone. Private consulting is a separate, optional service and is never a prerequisite for enrollment."],
];

const PATHS3 = [
  ["Work With Andrea", "Personal help for your baby or toddler's sleep. I look at your family's situation with you and we decide on next steps together. Virtual consulting is available anywhere. In-home support is offered in the Denver metro area.", "Explore Personal Sleep Help", "/pricing", "path-consulting", "Consulting"],
  ["Learn at Your Own Pace", "Online courses on naps, night wakings, regressions and bedtime. Choose Independent Study, which opens as soon as you enroll, or Faculty-Led Study with four weekly online meetings with Andrea. Courses are education and don't include private consulting.", "Explore Sleep Courses", "/courses", "path-courses", "Courses · From $99"],
  ["Find Answers, Free", "Practical guides on newborn, infant and toddler sleep that you can read right now, with no account and no payment. The Free Sleep Tracker is optional and only needs a free account.", "Browse Free Sleep Guides", "/resources", "path-guides", "Free"],
];

function SearchIntent({ nav }) {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16" data-testid="home-search-intent">
      <h2 className="font-academic text-3xl sm:text-4xl font-semibold text-[#F5F2E9] max-w-3xl">Searching for an infant sleep consultant near me? Choose the help that fits your family.</h2>
      <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid="home-pathways">
        {PATHS3.map(([t, d, cta, to, id, tag]) => (
          <div key={id} className="rounded-xl border border-[#BDA677]/30 bg-[#1F3445] p-7 sm:p-8 flex flex-col hover:border-[#BDA677]/60 transition-colors" data-testid={id}>
            <div className="eyebrow text-[#BDA677] mb-3" data-testid={`${id}-tag`}>{tag}</div>
            <div className="font-academic text-[1.7rem] font-semibold text-[#F5F2E9] leading-tight">{t}</div>
            <p className="text-[15px] text-[#C9CFD6] mt-3 leading-relaxed">{d}</p>
            <div className="mt-auto pt-6">
              <button onClick={() => nav(to)} data-testid={`${id}-btn`} className="btn-clay w-full px-5 py-3 text-[15px] inline-flex items-center justify-center gap-2">{cta} <ArrowRight size={16} /></button>
              {id === "path-consulting" && <a href="https://thenurturedparentco.com/" target="_blank" rel="noopener" data-testid="path-tnpc-link" onClick={() => trackEvent("outbound_consulting_click", { location: "home_pathway_card" })} className="block mt-3 text-[13px] text-[#8F9AA8] hover:text-[#F5F2E9] underline underline-offset-4">Or visit The Nurtured Parent Co., Andrea's separate practice</a>}
              {id === "path-guides" && <button onClick={() => nav(TRACKER_LINK)} data-testid="path-tracker-btn" className="block mt-3 text-left text-[13px] text-[#8F9AA8] hover:text-[#F5F2E9] underline underline-offset-4">Or try the Free Sleep Tracker (free account)</button>}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[13px] text-[#8F9AA8] mt-7 max-w-3xl leading-relaxed" data-testid="home-intent-consulting-note">Courses and the services on our Sleep Help page are purchased from Infant Sleep Consultant Near Me. Looking for personal guidance? Andrea Hurley also provides private pediatric sleep consulting through The Nurtured Parent Co., her separate consulting practice. Courses never include or require private consulting.</p>
    </section>
  );
}

export default function Home() {
  const [reviews, setReviews] = useState([]);

  useSeo({
    title: "Infant Sleep Consultant Near Me | Start Today",
    description: "Looking for an infant sleep consultant near me? Start online courses today for night wakings, naps, regressions and bedtime, available wherever you are.",
    canonical: "/",
  });

  useEffect(() => {
    http.get("/reviews").then((r) => setReviews(r.data.filter((x) => !x.sample && !x.demo))).catch(() => {});
    if (window.location.hash) {
      setTimeout(() => document.querySelector(window.location.hash)?.scrollIntoView(), 400);
    }
  }, []);

  return (
    <MarketingShell>
      <HomeBody reviews={reviews} />
    </MarketingShell>
  );
}

function HomeBody({ reviews }) {
  const nav = useNavigate();
  return (
    <>
      <StructuredData data={{ "@context": "https://schema.org", "@type": "WebSite", name: "Infant Sleep Consultant Near Me", url: "https://infantsleepconsultantnearme.com/" }} />
      <StructuredData data={{
        "@context": "https://schema.org", "@type": ["Organization", "EducationalOrganization"],
        name: "Infant Sleep Consultant Near Me", url: "https://infantsleepconsultantnearme.com/",
        description: "Expert infant sleep guidance for parents: five online infant sleep courses developed by Andrea Hurley, Certified Pediatric Sleep Consultant, with optional faculty-led online meetings, free student registration and a free Infant Sleep Tracker.",
      }} />

      <Hero nav={nav} />
      <SearchIntent nav={nav} />
      <Instructor />
      <StartHere />
      <CourseDetails />
      <FreeTracker nav={nav} />
      {reviews.length > 0 && <Reviews reviews={reviews} />}
      <Faq />

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 border-t border-[#2B4053] text-center" data-testid="home-final-cta">
        <h2 className="font-academic text-4xl sm:text-5xl font-semibold text-[#F5F2E9]">Wherever you're starting from, there's a next step</h2>
        <p className="text-[#B9C0C9] mt-3 max-w-2xl mx-auto">Work with me personally, learn through a course, or start with a free guide tonight.</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button data-testid="final-consulting" onClick={() => nav("/pricing")} className="btn-clay px-7 py-4 text-[15px] flex items-center justify-center gap-2">Work With Andrea <ArrowRight size={17} /></button>
          <button data-testid="final-find-course" onClick={() => nav("/courses")} className="btn-outline-night px-7 py-4 text-[15px]">Explore Sleep Courses</button>
          <button data-testid="final-free-guides" onClick={() => nav("/resources")} className="btn-outline-night px-7 py-4 text-[15px]">Browse Free Sleep Guides</button>
        </div>
        <div className="flex items-center gap-2 justify-center mt-10 text-[#8F9AA8] text-sm">
          <ShieldCheck size={16} /> Infant sleep education is not medical care. Health concerns should be discussed with your pediatrician.
        </div>
      </section>
      <ConsultingSummary />
    </>
  );
}

function ConsultingSummary() {
  return (
    <section id="consulting" className="scroll-mt-24 max-w-7xl mx-auto px-5 sm:px-8 py-14 border-t border-[#2B4053]" data-testid="home-consulting">
      <div className="eyebrow text-[#8F9AA8] mb-3">Separate Service · Optional</div>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-5" data-testid="home-tnpc">
        <p className="text-[#8F9AA8] text-sm leading-relaxed max-w-3xl">Looking for personal guidance? Andrea Hurley also provides private pediatric sleep consulting through <span className="text-[#F5F2E9]">The Nurtured Parent Co.</span>, her separate consulting practice. Bookings made there are handled by The Nurtured Parent Co., not by Infant Sleep Consultant Near Me. Private consulting is never part of course tuition.</p>
        <a href="https://thenurturedparentco.com/" target="_blank" rel="noopener" data-testid="tnpc-cta" onClick={() => trackEvent("outbound_consulting_click", { location: "private_consulting_section" })}
          className="btn-outline-night px-5 py-3 text-sm text-center whitespace-nowrap shrink-0">About Private Consulting</a>
      </div>
    </section>
  );
}

function CourseDetails() {
  return (
    <details className="group border-y border-[#BDA677]/25 bg-[#13242F]" data-testid="home-course-details">
      <summary className="max-w-7xl mx-auto px-5 sm:px-8 py-7 cursor-pointer list-none flex items-center justify-between gap-4" data-testid="home-course-details-toggle">
        <span><span className="eyebrow text-[#BDA677] block mb-1">For parents considering a course</span><span className="font-academic text-2xl sm:text-3xl text-[#F5F2E9] font-semibold">Course formats, enrollment & completion</span></span>
        <ArrowRight size={20} className="text-[#BDA677] shrink-0 transition-transform group-open:rotate-90" />
      </summary>
      <LearningFormats />
      <LearningExperience />
    </details>
  );
}

function SectionHead({ eyebrow, title, sub, light = false }) {
  return (
    <div className="mb-10 max-w-3xl">
      <div className={`eyebrow ${light ? "text-[#9C8556]" : "text-[#BDA677]"} mb-4 flex items-center gap-3`}><span className="w-8 h-px bg-[#BDA677]" />{eyebrow}</div>
      <h2 className={`font-academic text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] ${light ? "text-[#172A3A]" : "text-[#F5F2E9]"}`}>{title}</h2>
      {sub && <p className={`mt-4 text-base sm:text-[17px] leading-relaxed ${light ? "text-[#647080]" : "text-[#B9C0C9]"}`}>{sub}</p>}
    </div>
  );
}

function Hero({ nav }) {
  return (
    <section className="relative overflow-hidden" data-testid="home-hero">
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-8 pb-14 lg:pt-10 lg:pb-16 grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-14 items-center">
        <div className="fade-up">
          <div className="flex items-center gap-3 mb-5"><Crest size={30} /><div className="eyebrow text-[#BDA677] leading-relaxed">Infant & Toddler Sleep Help</div></div>
          <h1 className="font-academic text-[2.4rem] sm:text-5xl lg:text-[3.3rem] font-semibold tracking-tight text-[#F5F2E9] leading-[1.04]">
            Infant Sleep Consultant Near Me <span className="italic font-medium text-[#BDA677]">— Help for Your Family, Your Way</span>
          </h1>
          <p className="mt-5 font-academic text-[1.55rem] sm:text-[1.75rem] text-[#F5F2E9] leading-snug max-w-xl" data-testid="hero-promise">The Infant Sleep Help You Need, Available Wherever You Are.</p>
          <div className="mt-5 pl-5 border-l border-[#BDA677]/40 space-y-2.5 text-[#C9CFD6] text-[15px] sm:text-[16px] leading-[1.62] max-w-[37rem]" data-testid="hero-description">
            <p>When your baby isn't sleeping, figuring out where to turn shouldn't be another thing keeping you awake.</p>
            <p data-testid="hero-instructor">I'm <a href="#instructor" className="text-[#F5F2E9] font-semibold underline-offset-4 hover:underline">Andrea Hurley</a>, a Certified Pediatric Sleep Consultant, and I believe every family deserves guidance that makes sense for their child and their life.</p>
            <p className="text-[#F5F2E9]">Whether you're looking for personal sleep consulting, want to learn through a structured course, or simply need a clear answer to what's happening tonight, you can find a place to start here.</p>
          </div>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button data-testid="hero-consulting" onClick={() => nav("/pricing")}
              className="btn-clay px-7 py-4 text-[15px] flex items-center justify-center gap-2">Work With Andrea <ArrowRight size={17} /></button>
            <button data-testid="hero-explore-library" onClick={() => document.getElementById("start-here")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-outline-night px-7 py-4 text-[15px]">Find the Right Sleep Course</button>
          </div>
          <p className="text-[#8F9AA8] text-[13px] mt-5 tracking-wide flex flex-wrap items-center gap-x-2.5 gap-y-1" data-testid="hero-reassurance">
            <a href="/resources" data-testid="hero-free-guides" className="text-[#E4D3AC] underline underline-offset-4 hover:text-[#F5F2E9]">Browse Free Sleep Guides</a><span className="text-[#BDA677]" aria-hidden="true">·</span><span>Courses: Enroll Online, Learn at Your Own Pace</span>
          </p>
        </div>
        <div className="fade-up relative" style={{ animationDelay: "150ms" }}>
          <div className="relative border border-[#BDA677]/50 p-2 rounded-sm">
            <picture>
              <source srcSet="/images/parent-home-learning-v2.webp" type="image/webp" />
              <img src="/images/parent-home-learning-v2.jpg" width="848" height="1264" fetchpriority="high" decoding="async" alt="A mother at home using her laptop for an online infant sleep course while her awake baby plays on a play mat beside her" className="w-full h-[400px] sm:h-[500px] object-cover object-[50%_40%]" data-testid="hero-image" />
            </picture>
          </div>
          <div className="relative mx-4 -mt-10 sm:mx-0 sm:mt-0 sm:absolute sm:-bottom-6 sm:-left-8 bg-[#F5F2E9] text-[#172A3A] px-6 py-5 sm:max-w-[280px] shadow-2xl border-l-4 border-[#BDA677]" data-testid="hero-overlay">
            <div className="font-mono text-[11px] tracking-[0.2em] text-[#9C8556]">START LEARNING TODAY</div>
            <div className="font-academic text-xl font-semibold mt-1 leading-tight">5 Infant Sleep Courses</div>
            <div className="text-[12px] text-[#647080] mt-1">Self-Paced Learning Available Immediately</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FacultyVideo() {
  const [play, setPlay] = useState(false);
  const id = "nlbMwGPc0Ho";
  return (
    <div className="w-full max-w-[300px] mx-auto border border-[#BDA677] p-2 bg-white shadow-xl" data-testid="faculty-video">
          <div className="relative w-full aspect-[9/16] bg-[#172A3A]">
            {play ? (
              <iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0&playsinline=1`} referrerPolicy="strict-origin-when-cross-origin"
                title="A Welcome From Our Founding Instructor | Andrea Hurley" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen data-testid="faculty-video-iframe" />
            ) : (
              <button onClick={() => setPlay(true)} data-testid="faculty-video-play" aria-label="Play welcome message from Andrea Hurley" className="group absolute inset-0 w-full h-full">
                <img src={`https://i.ytimg.com/vi/${id}/oardefault.jpg`} alt="Andrea Hurley welcome video" loading="lazy" className="w-full h-full object-cover" />
                <span className="absolute inset-0 bg-[#172A3A]/25 group-hover:bg-[#172A3A]/10 transition-colors" />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-[#172A3A]/90 border-2 border-[#BDA677] grid place-items-center transition-transform duration-300 group-hover:scale-105">
                  <Play size={24} className="text-[#BDA677] fill-[#BDA677] ml-1" />
                </span>
              </button>
            )}
          </div>
    </div>
  );
}

function Instructor() {
  return (
    <section id="instructor" className="scroll-mt-24 bg-[#FBF9F4]" data-testid="home-instructor">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-[300px_1fr] gap-10 lg:gap-16 items-center">
        <FacultyVideo />
        <div>
          <div className="eyebrow text-[#9C8556] mb-4 flex items-center gap-3"><span className="w-8 h-px bg-[#BDA677]" />Meet Your Instructor</div>
          <h2 className="font-academic text-4xl sm:text-5xl font-semibold text-[#172A3A] leading-[1.05]" data-testid="instructor-name">Andrea Hurley</h2>
          <p className="mt-2 text-[#9C8556] text-sm uppercase tracking-[0.16em]" data-testid="instructor-credential">Founding Instructor · Certified Pediatric Sleep Consultant</p>
          <p className="mt-5 text-[#647080] text-base leading-relaxed max-w-2xl">
            Andrea wrote the curriculum, teaches every faculty-led class and personally reviews every final assessment. Her teaching begins from a single principle: understand how a baby's sleep works before changing it. Watch her welcome message to hear how she approaches infant sleep education.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/courses" data-testid="instructor-courses" className="bg-[#172A3A] text-[#F5F2E9] px-6 py-3.5 text-sm text-center hover:bg-[#22394D] transition-colors">View Andrea's Courses</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function LearningExperience() {
  const steps = [
    [BookOpen, "Choose a course", "Pick the subject that fits your questions. Any course can be taken first."],
    [GraduationCap, "Enroll", "Independent Study or Faculty-Led, with tuition shown on each course page."],
    [NotebookPen, "Study lessons", "Lessons, readings and coursework open from day one, at your own pace."],
    [Video, "Complete assessments", "Each lesson has a short knowledge assessment, then a final assessment."],
    [MessageSquareText, "Receive faculty feedback", "Andrea reviews your final assessment and releases your grade and remarks."],
    [Award, "Complete your course", "Your completion is recorded in your student record, with a certificate."],
  ];
  return (
    <section id="live-classes" className="scroll-mt-24 border-y border-[#BDA677]/25 bg-[#13242F]" data-testid="home-how-it-works">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <SectionHead eyebrow="Your Learning Experience" title="From your first lesson to course completion"
          sub="Every Infant Sleep Consultant Near Me course follows the same path, whether you study independently or with a faculty-led group." />
        <ol className="grid sm:grid-cols-2 lg:grid-cols-6 gap-px bg-[#2B4053]">
          {steps.map(([Icon, t, d], i) => (
            <li key={t} className="bg-[#13242F] p-6" data-testid={`model-step-${i + 1}`}>
              <div className="flex items-center justify-between"><Icon size={20} className="text-[#BDA677]" /><span className="font-academic text-[#BDA677]/70 text-lg">{String(i + 1).padStart(2, "0")}</span></div>
              <div className="font-academic text-xl text-[#F5F2E9] font-semibold mt-4">{t}</div>
              <p className="text-[#8F9AA8] text-sm mt-1.5 leading-relaxed">{d}</p>
            </li>
          ))}
        </ol>
        <Link to="/how-it-works" data-testid="home-how-it-works-link" className="inline-flex items-center gap-2 mt-8 text-[#BDA677] text-sm uppercase tracking-[0.14em] border-b border-[#BDA677] pb-1 hover:text-[#CDB98E]">How it works in detail <ArrowRight size={15} /></Link>
        <div className="mt-12 border border-[#BDA677]/30 p-7 sm:p-10" data-testid="home-interest">
          <div className="eyebrow text-[#BDA677] mb-3">Rolling Enrollment</div>
          <h3 className="font-academic text-3xl sm:text-4xl text-[#F5F2E9] font-semibold leading-tight">Start any course today, in either format</h3>
          <p className="text-[#B9C0C9] mt-4 leading-relaxed max-w-3xl">Independent Study opens immediately after enrollment. Faculty-Led Study opens the full course immediately too, and Andrea personally arranges your four weekly online meetings with you after you enroll. A group can run with just one family, and holds up to six.</p>
          <button onClick={() => document.getElementById("start-here")?.scrollIntoView({ behavior: "smooth" })} data-testid="home-rolling-find-course" className="btn-clay px-6 py-3.5 text-[15px] mt-6 inline-flex items-center gap-2">Find My Course <ArrowRight size={17} /></button>
        </div>
      </div>
    </section>
  );
}

function FreeTracker({ nav }) {
  return (
    <section id="tracker" className="scroll-mt-24 max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="home-learning-center">
      <div className="grid lg:grid-cols-[1fr_1fr] gap-12 items-center">
        <div>
          <SectionHead eyebrow="Free for Every Registered Parent" title="The free Infant Sleep Tracker"
            sub="Record your baby's nights and naps and see the pattern over time. The tracker is free for every registered parent, including families who never purchase a course, and your saved history stays private to your account." />
          <ul className="grid sm:grid-cols-2 gap-3 mb-8">
            {["Free with a student account", "No course purchase required", "Saved sleep history", "Private to your account", "Used in every course's coursework", "Course syllabi and previews"].map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-[#F5F2E9] text-[15px]"><ShieldCheck size={17} className="text-[#BDA677] mt-0.5 shrink-0" /> {b}</li>
            ))}
          </ul>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => nav(TRACKER_LINK)} data-testid="home-lc-create-account" className="btn-clay px-6 py-3.5 text-[15px] flex items-center justify-center gap-2">Use the Free Infant Sleep Tracker <ArrowRight size={17} /></button>
            <button onClick={() => nav("/login?next=/portal/progress")} data-testid="home-lc-signin" className="btn-outline-night px-6 py-3.5 text-[15px]">Already registered? Sign in</button>
          </div>
        </div>
        <div>
          <SleepTimeline />
          <p className="text-[#8F9AA8] text-xs mt-3 leading-relaxed text-center">
            A single night is one observation. Recording several nights shows the pattern.
          </p>
          <p className="text-[#B9C0C9] text-xs mt-2 leading-relaxed text-center" data-testid="tracker-save-note">
            Create a free account to save your sleep history and track patterns over time.
          </p>
        </div>
      </div>
    </section>
  );
}

function Reviews({ reviews }) {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 border-t border-[#2B4053]">
      <div className="eyebrow text-[#BDA677] mb-6">Reflections from families</div>
      <div className="grid md:grid-cols-3 gap-5">
        {reviews.slice(0, 3).map((r) => (
          <div key={r.id} className="card-night p-6">
            <div className="flex gap-1 mb-3">{Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={i} size={15} className="fill-[#BDA677] text-[#BDA677]" />)}</div>
            <p className="text-[#F5F2E9] font-academic text-lg leading-relaxed">"{r.text}"</p>
            <p className="text-[#8F9AA8] text-sm mt-4">— {r.name}, via {r.source}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="bg-[#F5F2E9]" data-testid="home-faq">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20">
        <SectionHead light eyebrow="Parent Questions" title="Consulting, courses & tuition" />
        <div className="divide-y divide-[#172A3A]/12 border-y border-[#172A3A]/12">
          {FAQ.map(([q, a]) => (
            <div key={q} className="py-6">
              <p className="font-academic text-2xl font-semibold text-[#172A3A]">{q}</p>
              <p className="text-[#647080] text-[15px] leading-relaxed mt-2">{a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
